import React, { useState } from 'react';
import { Reorder } from 'framer-motion';
import { ArrowLeftIcon } from 'lucide-react';
import type { Deliverable } from '../../types/portal';
import { usePortal } from '../../contexts/PortalContext';
import { AddDeliverable } from '../../components/portal/AddDeliverable';
import { DeliverableAdmin } from '../../components/portal/DeliverableAdmin';
import { ProjectFields } from '../../components/portal/ProjectFields';
import { projectStatus } from '../../utils/status';
import { ClientLink } from '../../components/portal/ClientLink';
import { ShowLink } from '../../components/portal/ShowLink';
import { ShowFields } from '../../components/portal/ShowFields';
import { PortalTeam } from '../../components/portal/PortalTeam';
import { DeleteProjectPanel } from '../../components/portal/ProjectOptions';
import { BUTTON_QUIET, BUTTON_SOLID, FIELD } from '../../components/portal/adminStyles';

/**
 * **THE WRAP-UP IS NOT HERE.** It was, for about an hour on 26 Sep 2026 — a
 * sixth section between Portal Team and the settings row. Tom: *"I don't
 * want it under the projects - it's not easy to find."* It is now the
 * Wrap-ups tab in the studio nav, `pages/portal/WrapUps.tsx`, which also
 * answers the question no per-project screen could: which finished projects
 * still have no report. Call Sheets left this page on 21 Sep for the same
 * reason. Resist putting the next occasional thing here too.
 */

interface ProjectAdminProps {
  /**
   * True when rendered inline inside another page — currently, an episode
   * expanded on the Show Admin page. Suppresses the "Client Projects" back
   * button (the episode row itself is already the way to collapse this)
   * and the top margin that assumes being the page's first element, so
   * clicking to switch episodes never jumps further away than the row that
   * was clicked. Also suppresses the show-level "Edit Show" and "Add
   * Episode" actions in the settings row — embedded only ever happens from
   * inside Show Admin, which already shows both of those once, at the page
   * level, so repeating them per episode is pure duplication.
   */
  embedded?: boolean;
}
export function ProjectAdmin({
  embedded = false
}: ProjectAdminProps = {}) {
  const {
    activeProject,
    shows,
    goPortal,
    // duplicateProject stays available in context for a future project
    // options menu — it is simply not a prominent action here.
    saveAsTemplate,
    archiveProject,
    reopenProject,
    setDeliverableOrder,
    addEpisode,
    notify
  } = usePortal();
  /* One panel open at a time — rather than Edit / Save-as-Template /
     Archive-confirm / Delete-confirm being four independent toggles that
     could all be open together. */
  type SettingsPanel = 'none' | 'editing' | 'showEditing' | 'template' | 'archive' | 'episode' | 'delete';
  const [panel, setPanel] = useState<SettingsPanel>('none');
  const editing = panel === 'editing';
  const editingShow = panel === 'showEditing';
  const savingTemplate = panel === 'template';
  const confirmArchive = panel === 'archive';
  const addingEpisode = panel === 'episode';
  const confirmDelete = panel === 'delete';
  const [templateName, setTemplateName] = useState('');
  const [episodeName, setEpisodeName] = useState('');
  const [carryingId, setCarryingId] = useState<string | null>(null);
  const project = activeProject;
  const total = project.deliverables.length;
  const hidden = project.deliverables.filter(item => !item.visibleToClient).length;
  /* When this project belongs to a show, the show link — not this project's
     own link — is the one thing to send the client: it stays valid across
     every episode, so staff never hand out a fresh link per episode. */
  const show = project.showId ? shows.find(item => item.id === project.showId) ?? null : null;
  function saveTemplate(event: React.FormEvent) {
    event.preventDefault();
    const name = templateName.trim();
    if (!name) return;
    saveAsTemplate(name);
    setTemplateName('');
    setPanel('none');
    notify(`Template saved · ${name}`);
  }
  function submitEpisode(event: React.FormEvent) {
    event.preventDefault();
    const name = episodeName.trim();
    if (!name) return;
    addEpisode(name);
    setEpisodeName('');
    setPanel('none');
    notify(project.showId ? `${name} added to the show` : `${project.name} is now a show · ${name} added`);
  }
  return <div data-id="element-2410" data-mp="393ymwjrt27dy">
      {!embedded && <button type="button" onClick={() => goPortal('projects')} className="label group flex items-center gap-3 text-paper/40 transition-colors duration-150 ease-out hover:text-paper" data-id="element-2411" data-mp="392xcmhim5oxa">
          <ArrowLeftIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:-translate-x-1" strokeWidth={1.25} data-id="element-2412" data-mp="3n2ycbtn56qb6" />
          Client Projects
        </button>}

      {/* The project itself is the anchor; everything else sits quietly under it.
          Embedded (an episode expanded inline on Show Admin) skips the client
          label and name — both are already showing one level up, on the show
          row and its header — so only what's specific to this episode remains. */}
      <header className={embedded ? '' : 'mt-8'} data-id="element-2413" data-mp="1bged62zzarzi">
        {!embedded && <>
            <p className="label text-paper/40" data-id="element-2414" data-mp="16hkza9gs4yyt">{project.client}</p>
            <h1 className="mt-5 font-display text-4xl leading-[0.95] tracking-tight sm:text-6xl" data-id="element-2415" data-mp="2440fz91dtslb">
              {project.name}
            </h1>
          </>}
        <p className={['label text-paper/40', embedded ? '' : 'mt-6'].join(' ')} data-id="element-2416" data-mp="1z5yukhghhh6o">
          {project.type} · {projectStatus(project)} · {total} deliverable{total === 1 ? '' : 's'} ·
          Last updated {project.updated}
          {project.archived ? ' · Archived' : ''}
        </p>

        {/* Sending the link and checking what it opens are the two things
            staff do most here, so both sit at the top. Preview renders the
            client portal itself, read-only, so it cannot drift out of step.
            A project that belongs to a show sends the show's one permanent
            link instead of a per-project link — the client picks the
            episode from inside it, so there's only ever one URL to keep
            track of. */}
        {show ? <ShowLink slug={show.slug} showId={show.id} data-id="element-2417" data-mp="2wxf19wfmetz7" /> : <ClientLink slug={project.slug} projectId={project.id} data-id="element-2418" data-mp="2i9alb07ycwk4" />}
      </header>

      {/* The production work leads the page. */}
      <section aria-labelledby="deliverables-admin" className="mt-12" data-id="element-2419" data-mp="2n1tvdn7fl9k6">
        <div className="flex flex-wrap items-baseline justify-between gap-4 border-b border-ruleDark pb-4" data-id="element-2420" data-mp="29a6zhohei31x">
          <h2 id="deliverables-admin" className="label m-0 text-paper/40" data-id="element-2421" data-mp="2e90czgwotu23">
            Deliverables
          </h2>
          {hidden > 0 && <p className="label-sm m-0 text-paper/35" data-id="element-2422" data-mp="1zdaq7gjohcwb">{hidden} hidden from client</p>}
        </div>

        {total === 0 ? <p className="border-b border-ruleDark py-10 font-display text-xl text-paper/40" data-id="element-2423" data-mp="1knhfvyhgh5qa">
            Nothing added yet.
          </p> : <Reorder.Group as="ul" axis="y" values={project.deliverables} onReorder={(next: Deliverable[]) => setDeliverableOrder(next.map(item => item.id))}
      /* A recessed field while a row is lifted, so the empty slot reads as a gap. */ className={['m-0 list-none p-0 transition-colors duration-150 ease-out', carryingId ? 'bg-paper/[0.02]' : ''].join(' ')} data-id="element-2424" data-mp="1goqmo2g4288g">
            {project.deliverables.map(item => <DeliverableAdmin key={item.id} deliverable={item} onLift={setCarryingId} data-id="element-2425" data-mp="28dbscexv4sa0" />)}
          </Reorder.Group>}

        <AddDeliverable data-id="element-2426" data-mp="2nbmbioseix65" />
      </section>

      <PortalTeam data-id="element-2427" data-mp="2ic9mbxe25e0e" />

      {/*
        Occasional project-level actions, deliberately quiet.
         **No heading (23 Sep 2026, Tom's request.)** It used to carry a
        "Project Settings" label, with Delete Project hidden below it behind a
        second heading and a second rule. Two headings and two rules for one
        short row of buttons is furniture, not structure: the rule above
        already separates this from the work, and every button says plainly
        what it does. The section keeps an aria-label so it is still named for
        anyone navigating by landmark.
       */}
      <section aria-label="Project settings" className="mt-16 border-t border-ruleDark pt-8" data-id="element-2428" data-mp="p80ugpzddfkt">
        <div className="flex flex-wrap items-center gap-x-8 gap-y-4" data-id="element-2429" data-mp="u7dgx9mbwkmv">
          <button type="button" onClick={() => setPanel(current => current === 'editing' ? 'none' : 'editing')} className={BUTTON_QUIET} data-id="element-2430" data-mp="u628uxluoqpy">
            {editing ? 'Close Editor' : 'Edit Project'}
          </button>
          {/* Edit Show and Add Episode are show-level actions. Embedded only
              ever happens inside Show Admin, which already shows both of
              these once at the page level — repeating them here, per
              episode, was only ever needed before episodes expanded inline
              (§30); now it's just two of everything on screen at once. */}
          {!embedded && show && <button type="button" onClick={() => setPanel(current => current === 'showEditing' ? 'none' : 'showEditing')} className={BUTTON_QUIET} data-id="element-2431" data-mp="25rhw4w85tyhh">
              {editingShow ? 'Close Editor' : 'Edit Show'}
            </button>}
          <button type="button" onClick={() => setPanel(current => current === 'template' ? 'none' : 'template')} className={BUTTON_QUIET} data-id="element-2432" data-mp="1s1o5q9h0b65n">
            Save as Template
          </button>
          {/* The call sheet and Shoot Day's crew/staff links both come from
              here — auto-filled from this project's own crew, location and
              schedule, so nothing about the shoot is entered twice. */}
          <button type="button" onClick={() => goPortal('callSheet')} className={BUTTON_QUIET} data-id="element-2433" data-mp="cpovs82wj2tm">
            Call Sheet / Shoot Day
          </button>
          {!embedded && <button type="button" onClick={() => setPanel(current => current === 'episode' ? 'none' : 'episode')} className={BUTTON_QUIET} data-id="element-2434" data-mp="qioai7uv0t04">
              {project.showId ? 'Add Episode' : 'Turn Into a Show'}
            </button>}
          {project.archived ? <button type="button" onClick={() => {
          reopenProject(project.id);
          notify('Project reopened · back in Active Projects');
        }} className={BUTTON_QUIET} data-id="element-2435" data-mp="7qly58p93yvo">
              Reopen Project
            </button> : <button type="button" onClick={() => setPanel('archive')} className={BUTTON_QUIET} data-id="element-2436" data-mp="1e5mngvjrtita">
              Archive Project
            </button>}
          {/* Last in the row, and dimmer than the rest. Archive is the normal
              action and this one is irreversible, so they should not read as
              equals even while they sit on the same line. The real guard is
              unchanged: the next screen asks you to type the project's name. */}
          <button type="button" onClick={() => setPanel(current => current === 'delete' ? 'none' : 'delete')} className="label text-paper/25 transition-colors duration-150 ease-out hover:text-paper/70" data-id="element-2437" data-mp="25do66dbnc7ut">
            Delete Project
          </button>
        </div>

        {confirmArchive && !project.archived && <div className="mt-8 max-w-xl border border-paper/30 px-6 py-6" data-id="element-2438" data-mp="2ocsc3d7w5li9">
            <p className="font-display text-2xl leading-snug" data-id="element-2439" data-mp="2obh574j5fxmx">Archive this project?</p>
            <p className="label-sm mt-4 leading-relaxed text-paper/40" data-id="element-2440" data-mp="2t53agbmzgz8p">
              It will be removed from Active Projects but its project history and data will be
              retained.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-6" data-id="element-2441" data-mp="2y17bqr5h49wp">
              <button type="button" onClick={() => {
            archiveProject();
            setPanel('none');
            notify('Project archived · nothing deleted');
            goPortal('projects');
          }} className={BUTTON_SOLID} data-id="element-2442" data-mp="2t09xuqrhy210">
                Archive Project
              </button>
              <button type="button" onClick={() => setPanel('none')} className={BUTTON_QUIET} data-id="element-2443" data-mp="2aa1zsn2nm1ro">
                Cancel
              </button>
            </div>
          </div>}

        {confirmDelete && <DeleteProjectPanel onClose={() => setPanel('none')} data-id="element-2444" data-mp="21byftv7bhd1z" />}

        {savingTemplate && <form onSubmit={saveTemplate} className="mt-8 max-w-md" data-id="element-2445" data-mp="1wder5n1c9n5x">
            <label className="block" data-id="element-2446" data-mp="bshyhp9e77jd">
              <span className="label block text-paper/40" data-id="element-2447" data-mp="gs2x1yknhdvk">Template Name</span>
              <input value={templateName} onChange={event => setTemplateName(event.target.value)} placeholder="e.g. 6-Episode Filmed Podcast" autoFocus className={FIELD} data-id="element-2448" data-mp="gu7bhag6dva3" />
            </label>
            <div className="mt-6 flex items-center gap-6" data-id="element-2449" data-mp="3p9r3651775n1">
              <button type="submit" className={BUTTON_SOLID} data-id="element-2450" data-mp="3t8q4zgrmuma9">
                Save Template
              </button>
              <button type="button" onClick={() => setPanel('none')} className={BUTTON_QUIET} data-id="element-2451" data-mp="225f40dbdket">
                Cancel
              </button>
            </div>
          </form>}

        {!embedded && addingEpisode && <form onSubmit={submitEpisode} className="mt-8 max-w-md" data-id="element-2452" data-mp="3ahym9olr9jj7">
            <label className="block" data-id="element-2453" data-mp="1h01de7gw8c4a">
              <span className="label block text-paper/40" data-id="element-2454" data-mp="1h12p8e5qh2n2">Episode Name</span>
              <input value={episodeName} onChange={event => setEpisodeName(event.target.value)} placeholder="e.g. Episode 2" autoFocus className={FIELD} data-id="element-2455" data-mp="1c1q0fnvaxlhu" />
            </label>
            <p className="label-sm mt-4 max-w-sm leading-relaxed text-paper/35" data-id="element-2456" data-mp="1l01tdwjn4vdg">
              {project.showId ? `Adds a new project alongside ${project.name} under the same show link.` : `Turns ${project.name} into a show, so this and the new episode share one client link.`}
            </p>
            <div className="mt-6 flex items-center gap-6" data-id="element-2457" data-mp="24pc7x77nha9z">
              <button type="submit" className={BUTTON_SOLID} data-id="element-2458" data-mp="24nr8p4ae535s">
                {project.showId ? 'Add Episode' : 'Create Show'}
              </button>
              <button type="button" onClick={() => setPanel('none')} className={BUTTON_QUIET} data-id="element-2459" data-mp="acmdmodddz22">
                Cancel
              </button>
            </div>
          </form>}

        {editing && <ProjectFields onClose={() => setPanel('none')} data-id="element-2460" data-mp="4bbzqqfyzmyx" />}
        {!embedded && editingShow && show && <ShowFields show={show} onClose={() => setPanel('none')} data-id="element-2461" data-mp="3mlnxld8n4si9" />}
      </section>
    </div>;
}